import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, Observable } from 'rxjs';
import {
  AppointmentType,
  DetailAttribute,
  DialogType,
  NavigationData,
  PtEntity,
  StoreTemplateStatus,
  UiDocumentItem,
} from '@pt/pt-models';
import { BusinessProjectManagementService, DeleteEntityServiceInterface, ImportDataService } from '@pt/api-client';
import { UserCheckedDialogComponent } from '../dialogs/user-checked-dialog.component';
import {
  ConfirmDeleteDataProtectionGovernanceItemDialogComponent
} from '../dialogs/confirm-delete-data-protection-governance-item-dialog.component';
import { WorkspaceItem } from '@primatoo-ms-organization';
import { ConfirmObsoleteDialogInterface, ConfirmWithValidation } from '../models';
import { ConfirmDialog } from '../models/confirm-dialog-interface';
import { ListUserForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/user/ListUserForMenuResponse';
import { IncidentTeam } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/IncidentTeam';
import { DataBreach } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/DataBreach';
import { TranslateService } from '@ngx-translate/core';
import { TextareaDialogComponent } from '../documents/dialogs/textarea-dialog.component';
import { PATreat } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/_summary/PATreat';
import {
  ListTemplateChaptersForDownload
} from 'it/primatoo/openapi/api/org/schemas/template/ListTemplateChaptersForDownload';
import {
  ListTemplateChapterResponse
} from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { Validated } from 'it/primatoo/openapi/api/org/schemas/common/docs/_findallvalidateddocresponse/Validated';
import { EditDocumentValidatedDialogComponent } from '../dialogs/edit-document-validated-dialog.component';
import { UpdateDataBreachRequest } from 'it/primatoo/openapi/api/org/schemas/databreachprocess/UpdateDataBreachRequest';
import { DocumentSettingsDialogComponent } from '../dialogs/document-settings-dialog.component';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import { HtmlHelpDialogComponent } from '../dialogs/html-help-dialog.component';
import { ConfirmObsoleteDialogComponent } from '../dialogs/confirm-obsolete-dialog.component';
import { SystemLogDialogComponent } from '../dialogs/system-log-dialog.component';
import { SelectChapterDialogComponent } from '@pt/pt-core';
import {
  AddNotesDialogComponent,
  ConfigureHelpDialogComponent,
  ConfirmDialogComponent,
} from '@pt/dialog';
import { ObsoleteDocumentDialogComponent } from '../dialogs/obsolete-document-dialog/obsolete-document-dialog.component';
import { CreateSnapshotDialogComponent } from '../dialogs/create-snapshot-dialog.component';
import { TreatmentInfoDialogComponent } from '../dialogs/treatment-info-dialog.component';
import { CreateDataBreachNoteDialogComponent } from '../dialogs/create-data-breach-note-dialog.component';
import { CreateFolderDialogComponent } from '../dialogs/create-folder-dialog.component';
import { ValidateRequestDialogComponent } from '../dialogs/validate-request-dialog.component';
import { EditEntityPriorityDialogComponent } from '../dialogs/edit-entity-priotity-dialog.component';
import { CreateSupplierDialogComponent } from '../dialogs/create-supplier-dialog.component';
import { SelectRecipientDialogComponent } from '../dialogs/select-recipient-dialog/select-recipient-dialog.component';
import { SelectUserResourceDialogComponent } from '../dialogs/select-user-resource-dialog/select-user-resource-dialog.component';
import { IncidentTeamDialogComponent } from '../dialogs/incident-team-dialog.component';
import { ShowAllAttributesDialogComponent } from '../dialogs/show-all-attributes-dialog.component';
import { ImportFileWithCheckComponent } from '../dialogs/import-file-with-check.component';
import { AttachFileDialogComponent } from '../dialogs/attach-file-dialog/attach-file-dialog.component';
import { MoveItemDialogComponent } from '../dialogs/move-item-dialog.component';
import { RenameResourceDialogComponent } from '../dialogs/rename-resource-dialog.component';
import { NewGroupDialogComponent } from '../dialogs/new-group-dialog.component';
import { UpdateDataBreachDialogComponent } from '../dialogs/update-data-breach-dialog.component';

const md = '500px';
const classMax = 'max-dialog';
const lg = 'lg';
const xl = 'xl';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(
    private modalService: NgbModal,
    private translateService: TranslateService
  ) {}

  runObsoleteTreatmentDialog = (
    treatmentType: TreatmentType,
    entity: PtEntity,
    deleteDocumentFn: (
      entity: PtEntity,
      docs: UiDocumentItem[]
    ) => Observable<boolean>
  ) => {
    const modalRef = this.modalService.open(ObsoleteDocumentDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: lg,
    });

    modalRef.componentInstance.treatmentType = treatmentType;
    modalRef.componentInstance.entity = entity;
    modalRef.componentInstance.deleteDocumentFn = deleteDocumentFn;

    return from(modalRef.result);
  };

  runCreateSnapshotDialog = (name: string) => {
    const modalRef = this.modalService.open(CreateSnapshotDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.run(name);
    return from(modalRef.result);
  };

  runTreatmentInfo = (treatmentInfo: Array<PATreat>) => {
    const modalRef = this.modalService.open(TreatmentInfoDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.run(treatmentInfo);
    return from(modalRef.result);
  };
  runDataBreachNotesDialog = (dataBreachId: number) => {
    const modalRef = this.modalService.open(
      CreateDataBreachNoteDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );
    modalRef.componentInstance.run(dataBreachId);
    return from(modalRef.result);
  };

  runCreateFolderDialog = (
    selectedFolderId: number,
    selectedLegalEntityId: number,
    isRequiredLegalEntity: boolean
  ) => {
    const modalRef = this.modalService.open(CreateFolderDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });

    modalRef.componentInstance.selectedFolderId = selectedFolderId;
    modalRef.componentInstance.selectedLegalEntityId = selectedLegalEntityId;
    modalRef.componentInstance.isRequiredLegalEntity = isRequiredLegalEntity;

    return from(modalRef.result);
  };

  runConfirmDeleteDataProtectionGovernanceItemDialog = (
    title: Observable<string>,
    message: Observable<string>,
    service: DeleteEntityServiceInterface,
    itemId: number,
    entityType: PtEntity
  ) => {
    const modalRef = this.modalService.open(
      ConfirmDeleteDataProtectionGovernanceItemDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.service = service;
    modalRef.componentInstance.itemId = itemId;
    modalRef.componentInstance.entityType = entityType;
    return from(modalRef.result);
  };

  runAccessDeniedDialog = () => {
    const title = this.translateService.stream(`TITLE.ACCESS_DENIED`);
    const message = this.translateService.stream(`MESSAGE.ACCESS_DENIED`);
    const modalRef = this.modalService.open(ConfirmDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.dialogType = DialogType.MESSAGE_FAILED;
    modalRef.componentInstance.validation = false;
    return from(modalRef.result);
  };

  runValidateRequestDialog = (
    title: string,
    message: string,
    info?: string
  ) => {
    const modalRef = this.modalService.open(ValidateRequestDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.run(title, message, info);
    return from(modalRef.result);
  };

  runEditDocumentValidated = (
    title: Observable<string>,
    docId: number,
    validated: Validated,
    service: BusinessProjectManagementService
  ) => {
    const modalRef = this.modalService.open(
      EditDocumentValidatedDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.run(docId, validated, service);
    return from(modalRef.result);
  };

  runDocumentSettingsDialog = () => {
    const modalRef = this.modalService.open(DocumentSettingsDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: lg,
    });
    return from(modalRef.result);
  };

  runConfirmDialog = (confirmDialog: ConfirmDialog) => {
    const modalRef = this.modalService.open(ConfirmDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: confirmDialog.size ? confirmDialog.size : md,
    });
    modalRef.componentInstance.title = confirmDialog.title;
    modalRef.componentInstance.message = confirmDialog.message;
    modalRef.componentInstance.dialogType = confirmDialog.dialogType;
    modalRef.componentInstance.subMessage = confirmDialog.subMessage;
    modalRef.componentInstance.customConfirmBtnLabel =
      confirmDialog.customConfirmBtnLabel;
    modalRef.componentInstance.validation = false;
    return from(modalRef.result);
  };

  runConfirmObsoleteDialog = (
    confirmDialog: ConfirmObsoleteDialogInterface
  ) => {
    const modalRef = this.modalService.open(ConfirmObsoleteDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: confirmDialog.size ? confirmDialog.size : md,
    });
    modalRef.componentInstance.title = confirmDialog.title;
    modalRef.componentInstance.message = confirmDialog.message;
    return from(modalRef.result);
  };

  runSelectTemplateChapterDialog = (
    response: ListTemplateChaptersForDownload | ListTemplateChapterResponse,
    title?: string,
    description?: string
  ) => {
    const modalRef = this.modalService.open(SelectChapterDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
    if (title) {
      modalRef.componentInstance.title = title;
    }
    if (description) {
      modalRef.componentInstance.description = description;
    }
    modalRef.componentInstance.run(response);
    return from(modalRef.result);
  };

  runSystemLog = () => {
    const modalRef = this.modalService.open(SystemLogDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: 'md',
    });
    return from(modalRef.result);
  };

  runConfirmDialogWithValidation = (
    confirmWithValidation: ConfirmWithValidation
  ) => {
    const modalRef = this.modalService.open(ConfirmDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: confirmWithValidation.size ? confirmWithValidation.size : md,
    });
    modalRef.componentInstance.validation = true;
    modalRef.componentInstance.title = confirmWithValidation.title;
    modalRef.componentInstance.message = confirmWithValidation.message;
    modalRef.componentInstance.dialogType = confirmWithValidation.dialogType;
    modalRef.componentInstance.subMessage = confirmWithValidation.subMessage;
    modalRef.componentInstance.textToValidate =
      confirmWithValidation.textToValidate;
    modalRef.componentInstance.customTemplate =
      confirmWithValidation.customTemplate;
    modalRef.componentInstance.customConfirmBtnLabel =
      confirmWithValidation.customConfirmBtnLabel;
    return from(modalRef.result);
  };

  runEditEntityPriorityDialog = (
    entity: PtEntity,
    payload: unknown,
    size?: 'md' | 'sm' | 'lg' | 'xl'
  ) => {
    const modalRef = this.modalService.open(EditEntityPriorityDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: size ? size : md,
    });

    modalRef.componentInstance.entity = entity;
    modalRef.componentInstance.payload = payload;

    return from(modalRef.result);
  };

  runCreateSupplierDialog = (createRequest?: {
    size?: 'md' | 'sm' | 'lg' | 'xl';
  }) => {
    const modalRef = this.modalService.open(CreateSupplierDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: createRequest?.size ? createRequest.size : md,
    });
    return from(modalRef.result);
  };

  runConfigureHelp = (
    title: Observable<string>,
    help: string,
    enableHtmlMode: boolean
  ) => {
    const modalRef = this.modalService.open(ConfigureHelpDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: lg,
    });
    modalRef.componentInstance.run(title, help, enableHtmlMode);
    return from(modalRef.result);
  };

  runHtmlHelp = (help: Observable<string>) => {
    const modalRef = this.modalService.open(HtmlHelpDialogComponent, {
      centered: true,
      size: lg,
    });
    modalRef.componentInstance.help = help;
    return from(modalRef.result);
  };

  runSelectRecipient = (
    title: Observable<string>,
    selectedRecipientId?: number
  ) => {
    const modalRef = this.modalService.open(SelectRecipientDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: lg,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.selectedRecipientId = selectedRecipientId;
    return from(modalRef.result);
  };

  runSelectUserResource = (
    title: Observable<string>,
    appointmentType: AppointmentType,
    selectedResourceId?: number
  ) => {
    const modalRef = this.modalService.open(SelectUserResourceDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: lg,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.appointmentType = appointmentType;
    modalRef.componentInstance.selectedResourceId = selectedResourceId;
    return from(modalRef.result);
  };

  runIncidentTeamDialog(
    menu: ListUserForMenuResponse,
    incidentTeam: IncidentTeam
  ) {
    const modalRef = this.modalService.open(IncidentTeamDialogComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: classMax,
    });
    modalRef.componentInstance.run(menu, incidentTeam);
    return from(modalRef.result);
  }

  runAddNotes(title: Observable<string | any>, notes: any) {
    const modalRef = this.modalService.open(AddNotesDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: lg,
    });
    modalRef.componentInstance.run(title, notes);
    return from(modalRef.result);
  }

  runShowAllAttributesDialog = (
    title: Observable<string>,
    attributes: DetailAttribute[]
  ) => {
    const modalRef = this.modalService.open(ShowAllAttributesDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.attributes = attributes;
    return from(modalRef.result);
  };

  runImportDialog = (
    title: Observable<string>,
    importService: ImportDataService,
    description?: Observable<string>
  ) => {
    const modalRef = this.modalService.open(ImportFileWithCheckComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.title = title;
    if (description) {
      modalRef.componentInstance.description = description;
    }
    modalRef.componentInstance.importService = importService;
    return from(modalRef.result);
  };

  runAttachDialog = (
    title: Observable<string>,
    hideWorkspaceTab: boolean = false,
    selectedFolderId?: number
  ) => {
    const modalRef = this.modalService.open(AttachFileDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: xl,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.hideWorkspaceTab = hideWorkspaceTab;
    modalRef.componentInstance.selectedFolderId = selectedFolderId;
    return from(modalRef.result);
  };

  runMoveItemDialog = (
    title: Observable<string>,
    resource: WorkspaceItem,
    folders: NavigationData[]
  ) => {
    const modalRef = this.modalService.open(MoveItemDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.resource = resource;
    modalRef.componentInstance.folders = folders;
    return from(modalRef.result);
  };

  runRenameWorkspaceItemDialog = (resource: WorkspaceItem) => {
    const modalRef = this.modalService.open(RenameResourceDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });

    modalRef.componentInstance.resource = resource;

    return from(modalRef.result);
  };

  runNewGroupDialog = (
    title: Observable<string>,
    message: Observable<string>,
    inputLabel: Observable<string>
  ) => {
    const modalRef = this.modalService.open(NewGroupDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.inputLabel = inputLabel;
    return from(modalRef.result);
  };

  closeAllDialog() {
    this.modalService.dismissAll();
  }

  runUserCheckDialog(document: any, state: StoreTemplateStatus) {
    const modalRef = this.modalService.open(UserCheckedDialogComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      size: 'md',
    });
    modalRef.componentInstance.document = document;
    modalRef.componentInstance.state = state;
    return from(modalRef.result);
  }

  runUpdateDataBreachDialog(
    databreach: DataBreach,
    request: UpdateDataBreachRequest
  ) {
    const modalRef = this.modalService.open(UpdateDataBreachDialogComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      size: 'md',
    });
    modalRef.componentInstance.run(databreach, request);
    return from(modalRef.result);
  }

  runTextArea = (
    title: Observable<string>,
    value: string,
    editable: boolean,
    saveBtnLabel?: Observable<string>
  ) => {
    const modalRef = this.modalService.open(TextareaDialogComponent, {
      backdrop: 'static',
      size: 'lg',
      keyboard: true,
      centered: true,
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.value = value;
    modalRef.componentInstance.editable = editable;
    if (saveBtnLabel) {
      modalRef.componentInstance.saveBtnLabel = saveBtnLabel;
    }
    return from(modalRef.result);
  };
}
